import { nanoid } from 'nanoid'
import React, { FC } from 'react'
import { View } from 'react-native'

import BADGE_PRESETS from '@common/components/BadgeWithPreset/presets'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { SPACING_MI } from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'

export type TypeBadgeSize = 'big' | 'small' | 'tiny'

// Geometry per tier. The badge should stay at roughly half the avatar's height,
// so avatars below ~28px need their own tier — `small` is tuned for 32px and
// looks oversized on a 24px avatar.
const SIZES: Record<
  TypeBadgeSize,
  {
    offset: number
    paddingHorizontal: number
    paddingVertical: number
    borderWidth: number
    fontSize: number
  }
> = {
  big: {
    offset: -SPACING_MI / 2,
    paddingHorizontal: 3,
    paddingVertical: 2,
    borderWidth: 3,
    fontSize: 10
  },
  small: {
    offset: -SPACING_MI,
    paddingHorizontal: 3,
    paddingVertical: 2,
    borderWidth: 2,
    fontSize: 9
  },
  tiny: {
    offset: -SPACING_MI / 2,
    paddingHorizontal: 1.5,
    paddingVertical: 0.5,
    borderWidth: 1.5,
    fontSize: 7
  }
}

interface Props {
  isSmart: boolean
  size?: TypeBadgeSize
  showTooltip?: boolean
}

const TypeBadge: FC<Props> = ({ isSmart, size, showTooltip = false }) => {
  const { theme, themeType } = useTheme()
  const badgePreset = BADGE_PRESETS['smart-account']
  const tooltipId = nanoid(6)
  const { offset, paddingHorizontal, paddingVertical, borderWidth, fontSize } =
    SIZES[size || 'small']

  return isSmart ? (
    <View
      dataSet={createGlobalTooltipDataSet({
        id: tooltipId,
        content: badgePreset.tooltipText,
        hidden: !showTooltip
      })}
      style={{
        position: 'absolute',
        left: offset,
        top: offset,
        paddingHorizontal,
        paddingVertical,
        backgroundColor: theme.successDecorative,
        zIndex: 2,
        borderRadius: 50,
        borderWidth,
        borderColor:
          themeType === THEME_TYPES.DARK ? theme.secondaryBackground : theme.primaryBackground
      }}
    >
      <Text color={theme.primaryBackground} weight="semiBold" fontSize={fontSize}>
        SA
      </Text>
    </View>
  ) : null
}

export default React.memo(TypeBadge)
